import Assignment from '../../../../types/functions/Assignment/Assignment';
import Condition from '../../../../types/functions/Condition/Condition';
import conditionHelper from './../condition/conditionHelper';
import copyObject from '../../common/copyObject';
import Operand from '../../../../types/functions/Operand/Operand';
import operandHelper from './../operand/operandHelper';
import ReportLogicLocation from '../../../../types/functions/Location/ReportLogicLocation';
import typeHelper from '../../common/typeHelper';
import Statement from '../../../types/Functions/Statement';
import Declaration from '../../../types/Functions/Declaration';
import ConditionAndAssignmentNumber from '../../../../components/Functions/Condition/Types/ConditionAndAssignmentNumber';
import locationType from '../../../../types/functions/Location/LocationType';
import functionName from '../../../constants/functions/definition/functionName';
import operandType from '../../../constants/functions/operandType';
import copyArray from '../../common/copyArray';

const getInner = (assignment: Assignment): Assignment | null => {

    if (typeHelper.isObject(assignment.elseIf)) {
        return assignment.elseIf;
    }

    if (typeHelper.isObject(assignment.else)) {
        return assignment.else;
    }

    return null;
};

const find = (assignment: Assignment, predicate: (x: Assignment) => boolean): Assignment | null => {
    do {
        if (predicate(assignment)) {
            return assignment;
        }

        assignment = getInner(assignment) as Assignment;
    }
    while (assignment != null);

    return null;
};

const getPrevious = (assignment: Assignment, number: number): Assignment | null => {

    return find(assignment, x => x.elseIf?.number === number || x.else?.number === number);
};

const getNext = (assignment: Assignment, number: number): Assignment | null => {

    let target = getByNumber(assignment, number);

    if (!typeHelper.isObject(target)) {
        return null;
    }

    if (typeHelper.isObject(target.elseIf)) {
        return target.elseIf;
    }

    if (typeHelper.isObject(target.else)) {
        return target.else;
    }

    return null;
};

const getLast = (assignment: Assignment): Assignment | null => {

    return find(assignment, x => !typeHelper.isObject(x.elseIf) && !typeHelper.isObject(x.else));
};

const asCollection = (assignment: Assignment): Assignment[] => {

    let collection: Assignment[] = [];

    if (!typeHelper.isObject(assignment)) {
        return collection;
    }

    do {
        collection.push(assignment);
        assignment = getInner(assignment) as Assignment;
    }
    while (assignment != null);

    return collection;
};

const getByNumber = (assignment: Assignment, number: number): Assignment => {

    return find(assignment, x => x.number === number) as Assignment;
};

const setMatchFunction = (operand: Operand) => {
    operand.function = functionName.match;
    operand.operandType = operandType.function;
    operand.arguments = (operand.arguments ?? [0]) as Operand[]
}

const add = (assignment: Assignment, number: number, doNotPopulate: boolean, location: ReportLogicLocation): void => {

    let isMatchingKey = location.statement === locationType.statement.matchingKey ||
        location.statement === locationType.statement.matchingKeyEndPoint;

    let empty = getEmpty(doNotPopulate);

    let last = getLast(assignment) as Assignment;

    let target = getByNumber(assignment, number) as Assignment;

    let previous = getPrevious(assignment, number) as Assignment;

    let isFirst = !typeHelper.isObject(previous);
    let isLast = target.number === last.number;

    if (isFirst) {

        if (isLast) {
            target.condition = conditionHelper.add(assignment.condition as Condition, null);
            target.else = empty;
            if (isMatchingKey) {
                setMatchFunction(target.else.value,);
                setMatchFunction(target.value);
            }
        }
        else {
            assignment.elseIf = copyObject(assignment);
            assignment.else = null;
            assignment.doNotPopulate = doNotPopulate;
            assignment.condition = conditionHelper.add(empty.condition as Condition, null);
            assignment.value = operandHelper.getEmpty();
            if (isMatchingKey) {
                setMatchFunction(assignment.value);
            }
        }
    }
    else {

        empty.condition = conditionHelper.add(empty.condition as Condition, null);

        previous.elseIf = empty;
        previous.else = null;
        if (isMatchingKey) {
            setMatchFunction(previous.elseIf.value);
        }

        if (isLast) {
            empty.else = last;
            if (isMatchingKey) {
                setMatchFunction(empty.else.value);
            }
        }
        else {
            empty.elseIf = target;
        }
    }

    asCollection(assignment).forEach((a, i) => a.number = i + 1);
};

const remove = (assignment: Assignment, number: number): void => {

    let previous = getPrevious(assignment, number) as Assignment;

    if (!typeHelper.isObject(previous)) {

        let target = getByNumber(assignment, number) as Assignment;

        target.condition = null;
        target.value = target.else?.value || {} as Operand
        target.else = null;

        return;
    }

    let removed = getByNumber(assignment, number);

    let next = getNext(assignment, number) as Assignment;

    previous.elseIf = null;
    previous.else = null;

    if (typeHelper.isObject(removed.elseIf)) {
        previous.elseIf = next;
    } else if (typeHelper.isObject(removed.else)) {
        previous.else = next;
    }
};

const getValue = (assignment: Assignment, location: ReportLogicLocation, recursive: boolean): Operand => {

    let target = getByNumber(assignment, location.assignmentNumber);

    if (!recursive) {
        return target.value;
    }

    return operandHelper.getOperand(target.value, location);
};

const getEmpty = (doNotPopulate: boolean): Assignment => {
    return {
        number: 0,
        condition: null,
        value: operandHelper.getEmpty(),
        doNotPopulate: doNotPopulate,
        elseIf: null,
        else: null
    };
};

const removeOnlyValues = (doNotPopulate: boolean, assignment: Assignment): Assignment => {

    assignment.value = operandHelper.getEmpty();

    if (assignment.else) {
        assignment.else.value = operandHelper.getEmpty();
    }
    else if (assignment.elseIf) {
        assignment.elseIf = removeOnlyValues(doNotPopulate, assignment.elseIf);
    }

    return assignment;
};

const compareAssignments = (left: Assignment, right: Assignment) => {
    return JSON.stringify(left) === JSON.stringify(right);
}

const clearOperandsInDeclaration = (location: ReportLogicLocation, declaration: Declaration, conditionNumber: number, isValue?: boolean): Declaration => {

    let newAssignment = copyObject(declaration.assignment);

    let assignment = assignmentHelper.getByNumber(newAssignment, location.assignmentNumber);

    if (isValue) {
        assignment.value = operandHelper.getEmpty();
        if(location.statement===locationType.statement.matchingKeyEndPoint||
            location.statement===locationType.statement.matchingKey) {
            setMatchFunction(assignment.value);
        }
        setMatchFunction(assignment.value);
    }
    else {
        clearCondition(assignment.condition as Condition, conditionNumber);
    }

    let newDeclaration = copyObject(declaration) as Declaration;

    newDeclaration.assignment = newAssignment;

    return newDeclaration;
}

const clearCondition = (condition: Condition, targetNumber: number) => {

    if (condition) {
        if (condition.number === targetNumber) {
            condition.operand1 = operandHelper.getEmpty();
            condition.operand2 = operandHelper.getEmpty();
        }

        if (condition.and) {
            clearCondition(condition.and, targetNumber);
        }

        if (condition.or) {
            clearCondition(condition.or, targetNumber);
        }
    }
}

const clearOperandsInStatementByConditionNumber = (conditionNumber: number, statement: Statement): Statement => {

    let newStatement = copyObject(statement);

    clearCondition(newStatement.condition as Condition, conditionNumber);

    return newStatement;
}

const getAssignmentValueOperands = (operand: Operand, operands: Operand[]): void => {
    operand.arguments?.forEach((o) => {

        operands.push(o);

        if (o.arguments?.length) {
            getAssignmentValueOperands(o, operands);
        }
    });
}

const getAllOperands = (assignment: Assignment): Operand[] => {

    let operands: Operand[] = [assignment.value];

    getAssignmentValueOperands(assignment.value, operands);

    traverseAssignmentConditions(assignment, (condition: Condition) => {

        operands = operands.concat(conditionHelper.getAllOperands(condition));
    })

    return operands;
}

const traverseCondition = (condition: Condition, assignmentNumber: number, onConditionFound: (condition: Condition, assignmentNumber: number) => any): boolean => {
    if (condition) {
        const stack: Condition[] = [condition];

        while (stack?.length > 0) {
            const currentCondition = stack.pop();

            let shoulBreak = onConditionFound(currentCondition as Condition, assignmentNumber);

            if (shoulBreak) {
                return true;
            }

            if (currentCondition?.and) {
                stack.push(currentCondition.and);
            }
            if (currentCondition?.or) {
                stack.push(currentCondition.or);
            }
        }
    }

    return false;
}

const traverseAssignmentConditions = (assignment: Assignment, onConditionFound: (condition: Condition, assignmentNumber: number) => any): void => {

    if (assignment.condition) {
        let shouldBreak = traverseCondition(assignment.condition, assignment.number, onConditionFound);
        if (shouldBreak) {
            return;
        }
    }

    let stack: Assignment[] = [assignment.elseIf as Assignment];

    while (stack?.length > 0) {
        const currentAssignment = stack.pop();

        if (currentAssignment?.condition) {
            let shouldBreak = traverseCondition(currentAssignment.condition, currentAssignment.number, onConditionFound);

            if (shouldBreak) {
                return;
            }
        }

        if (currentAssignment?.elseIf) {
            stack.push(currentAssignment.elseIf);
        }
        if (currentAssignment?.else) {
            stack.push(currentAssignment.else);
        }
    }
}

const getAllConditionsInAssignment = (assignment: Assignment): ConditionAndAssignmentNumber[] => {
    let conditions = [] as ConditionAndAssignmentNumber[];

    traverseAssignmentConditions(assignment, (condition: Condition, assignmentNumber: number) => {
        conditions.push({ condition, assignmentNumber });
    });

    return conditions;
}



const getEmptyAssignment = (): Assignment => {
    return {
        condition: null,
        doNotPopulate: false,
        else: null,
        elseIf: null,
        number: 1,
        value: {} as Operand
    }
}

const resetElseIfNumbers = (assignment: Assignment): void => {

    let number = 1;

    assignment.number = number;

    let elseIf = assignment.elseIf;

    while (elseIf) {

        number++;

        elseIf.number = number;
        elseIf = elseIf.elseIf;
    }
}

const assignmentHelper = {
    asCollection,
    getByNumber,
    add,
    remove,
    getValue,
    getEmpty,
    removeOnlyValues,
    compareAssignments,
    clearOperandsInStatementByConditionNumber,
    clearCondition,
    clearOperandsInDeclaration,
    getAllOperands,
    traverseAssignmentConditions,
    getAllConditionsInAssignment,
    getEmptyAssignment,
    resetElseIfNumbers
};

export default assignmentHelper;
